import { merge } from "lodash";

import {
  DEFAULT_RELIABILITY_TYPE,
  DEFAULT_TEST_METHOD,
  DEFAULT_TRANSDUCER_TYPE,
  QUICK_SIN_CONFIGURATION_METHOD_BINAURAL,
  QUICK_SIN_CONFIGURATION_METHOD_LEFT_RIGHT
} from "constants/audiology";
import {
  ACOUSTIC_REFLEX_DECAY_REPORT_SELECTED,
  OTOACOUSTIC_EMISSIONS_REPORT_SELECTED,
  OTOSCOPY_REPORT_SELECTED,
  PURE_TONE_AUDIOGRAM_REPORT_SELECTED,
  SPEECH_AUDIOLOGY_WORD_RECOGNITION_REPORT_SELECTED,
  SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION,
  SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST,
  SPEECH_IN_NOISE_REPORT_SELECTED,
  SPEECH_IN_NOISE_REPORT_UPDATE_METHOD,
  TYMPANOMETRY_REPORT_SELECTED,
  WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION,
  WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST,
  eventBus
} from "hooks/use-event-bus/event-bus";
import { type Report, type ReportInput } from "services/types/reports.type";
import type { MinimalStaffMember } from "services/types/staff.type";
import { getValueOrDefault, isNotBlank } from "utils/common";
import { isEmpty } from "utils/reports.utils";

import type { ReportSettingsRecord } from "./report-local-settings/index-db.types";
import { findReportSetting } from "./report-local-settings/report-local-settings-db.util";

interface PureToneAudiogramReport extends ReportSetting {
  is_air_conduction_l_selected: boolean;
  is_air_conduction_masking_level_l_selected: boolean;
  is_bone_conduction_l_selected: boolean;
  is_bone_conduction_masking_level_l_selected: boolean;
  is_mcl_l_selected: boolean;
  is_ucl_l_selected: boolean;
  is_sound_field_aided_l_selected: boolean;
  is_sound_field_unaided_l_selected: boolean;
  is_air_conduction_r_selected: boolean;
  is_air_conduction_masking_level_r_selected: boolean;
  is_bone_conduction_r_selected: boolean;
  is_bone_conduction_masking_level_r_selected: boolean;
  is_mcl_r_selected: boolean;
  is_ucl_r_selected: boolean;
  is_sound_field_aided_r_selected: boolean;
  is_sound_field_unaided_r_selected: boolean;
}

interface ReportSetting {
  is_selected: boolean;
}

interface AudiologyTestDefaults {
  pure_tone_audiogram_report: PureToneAudiogramReport;
  speech_audiometry_report: ReportSetting;
  word_recognition_report: ReportSetting;
  tympanometry_report: ReportSetting;
  acoustic_reflex_report: ReportSetting;
  acoustic_reflex_decay_report: ReportSetting;
  speech_in_noise_report: ReportSetting;
  otoacoustic_emissions_report: ReportSetting;
  otoscopy_report: ReportSetting;
  real_ear_measurement_report: ReportSetting;
}

export const getReportsDefaultsToApply = async (params: {
  report: Report;
  practiceId: number;
  patientId: number;
  reportId: number;
  user: MinimalStaffMember;
}) => {
  const { report, practiceId, patientId, reportId, user } = params;

  const baseSetting = {
    practiceId,
    patientId,
    reportId
  };

  const foundSetting = await findReportSetting(baseSetting);
  const setting: ReportSettingsRecord = merge({}, baseSetting, foundSetting);

  const pref = user.user_preferences?.reporting;
  let updatedReport: Partial<ReportInput> = {};

  let shouldUpdate = false;

  const testsEmpty =
    isEmpty(report.pureToneAudiogramReport) &&
    isEmpty(report.speechAudiometryReport) &&
    isEmpty(report.wordRecognitionReport) &&
    isEmpty(report.tympanometryReport) &&
    isEmpty(report.acousticReflexDecayReport) &&
    isEmpty(report.speechInNoiseReport) &&
    isEmpty(report.otoacoustic_emissions_report) &&
    isEmpty(report.otoscopy_report) &&
    isEmpty(report.real_ear_measurement_report);

  const allTestsSelected = [
    report.pureToneAudiogramReport?.isSelected,
    report.speechAudiometryReport?.isSelected,
    report.wordRecognitionReport?.isSelected,
    report.tympanometryReport?.isSelected,
    report.acousticReflexDecayReport?.isSelected,
    report.speechInNoiseReport?.isSelected,
    report.otoacoustic_emissions_report?.is_selected,
    report.otoscopy_report?.is_selected,
    report.real_ear_measurement_report?.is_selected
  ]
    .filter((value) => value !== undefined)
    .every(Boolean);

  const isCreatedBySystem = !Boolean(report.created_by);

  if (!foundSetting?.reliability && !report.reliability) {
    updatedReport.reliability = DEFAULT_RELIABILITY_TYPE;
    setting.reliability = true;
    shouldUpdate = true;
  }

  if (!foundSetting?.testMethod && !report.testMethod) {
    updatedReport.test_method = DEFAULT_TEST_METHOD;
    setting.testMethod = true;
    shouldUpdate = true;
  }

  if (!foundSetting?.transducer && !report.transducer) {
    updatedReport.transducer = DEFAULT_TRANSDUCER_TYPE;
    setting.transducer = true;
    shouldUpdate = true;
  }

  //set tests default isSelected values
  if (
    !setting?.testsCheckedByDefault &&
    (testsEmpty || (isCreatedBySystem && allTestsSelected))
  ) {
    if (
      isNotBlank(pref?.pure_tone_audiogram_report_selected) &&
      !pref?.pure_tone_audiogram_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        pure_tone_audiogram_report: {
          is_selected: false,
          is_air_conduction_l_selected: true,
          is_air_conduction_masking_level_l_selected: true,
          is_bone_conduction_l_selected: true,
          is_bone_conduction_masking_level_l_selected: true,
          is_mcl_l_selected: true,
          is_ucl_l_selected: true,
          is_sound_field_aided_l_selected: true,
          is_sound_field_unaided_l_selected: true,
          is_air_conduction_r_selected: true,
          is_air_conduction_masking_level_r_selected: true,
          is_bone_conduction_r_selected: true,
          is_bone_conduction_masking_level_r_selected: true,
          is_mcl_r_selected: true,
          is_ucl_r_selected: true,
          is_sound_field_aided_r_selected: true,
          is_sound_field_unaided_r_selected: true
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.speech_in_noise_report_selected) &&
      !pref?.speech_in_noise_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        speech_in_noise_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.acoustic_reflex_decay_report_selected) &&
      !pref?.acoustic_reflex_decay_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        acoustic_reflex_report: {
          is_selected: false
        },
        acoustic_reflex_decay_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.tympanometry_report_selected) &&
      !pref?.tympanometry_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        tympanometry_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.speech_audiometry_word_recognition_report_selected) &&
      !pref?.speech_audiometry_word_recognition_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          is_selected: false
        },
        word_recognition_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.otoacoustic_emissions_report_selected) &&
      !pref?.otoacoustic_emissions_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        otoacoustic_emissions_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }

    if (
      isNotBlank(pref?.otoscopy_report_selected) &&
      !pref?.otoscopy_report_selected
    ) {
      updatedReport = merge(updatedReport, {
        otoscopy_report: {
          is_selected: false
        }
      });
      setting.testsCheckedByDefault = true;
      shouldUpdate = true;
    }
  }

  //set speechInNoiseReport ear method
  if (
    !setting?.speechInNoiseMethod &&
    (updatedReport?.speech_in_noise_report?.is_selected ||
      report.speechInNoiseReport?.isSelected) &&
    isNotBlank(pref?.speech_in_noise?.method)
  ) {
    if (
      pref?.speech_in_noise?.quick_sin_default_configuration ===
        QUICK_SIN_CONFIGURATION_METHOD_BINAURAL &&
      !report.speechInNoiseReport?.binaural?.method &&
      report.speechInNoiseReport?.binaural?.snrLossDegree
    ) {
      updatedReport = merge(updatedReport, {
        speech_in_noise_report: {
          binaural: {
            method: pref?.speech_in_noise?.method,
            snr_loss_degree: report.speechInNoiseReport?.binaural?.snrLossDegree
          }
        }
      });
    }

    if (
      pref?.speech_in_noise?.quick_sin_default_configuration ===
        QUICK_SIN_CONFIGURATION_METHOD_LEFT_RIGHT &&
      !report.speechInNoiseReport?.leftEar?.method &&
      report.speechInNoiseReport?.leftEar?.snrLossDegree
    ) {
      updatedReport = merge(updatedReport, {
        speech_in_noise_report: {
          left_ear: {
            method: pref?.speech_in_noise?.method,
            snr_loss_degree: report.speechInNoiseReport?.leftEar?.snrLossDegree
          }
        }
      });
    }

    if (
      pref?.speech_in_noise?.quick_sin_default_configuration ===
        QUICK_SIN_CONFIGURATION_METHOD_LEFT_RIGHT &&
      !report.speechInNoiseReport?.rightEar?.method &&
      report.speechInNoiseReport?.rightEar?.snrLossDegree
    ) {
      updatedReport = merge(updatedReport, {
        speech_in_noise_report: {
          right_ear: {
            method: pref?.speech_in_noise?.method,
            snr_loss_degree: report.speechInNoiseReport?.rightEar?.snrLossDegree
          }
        }
      });
    }
    setting.speechInNoiseMethod = true;
    shouldUpdate = true;
  }

  //set speech_audiometry ear presentation
  if (
    !setting?.speechAudiometryPresentation &&
    (updatedReport?.speech_audiometry_report?.is_selected ||
      report.speechAudiometryReport?.isSelected) &&
    isNotBlank(pref?.speech_audiometry?.presentation)
  ) {
    if (
      !report.speechAudiometryReport?.leftEar?.presentation &&
      report.speechAudiometryReport?.leftEar?.wordList &&
      report.speechAudiometryReport?.leftEar?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          left_ear: {
            presentation: pref?.speech_audiometry?.presentation,
            type: report.speechAudiometryReport?.leftEar?.type,
            wordList: report.speechAudiometryReport?.leftEar?.wordList
          }
        }
      });
    }

    if (
      !report.speechAudiometryReport?.binaural?.presentation &&
      report.speechAudiometryReport?.binaural?.wordList &&
      report.speechAudiometryReport?.binaural?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          binaural: {
            presentation: pref?.speech_audiometry?.presentation,
            type: report.speechAudiometryReport?.binaural?.type,
            wordList: report.speechAudiometryReport?.binaural?.wordList
          }
        }
      });
    }

    if (
      !report.speechAudiometryReport?.rightEar?.presentation &&
      report.speechAudiometryReport?.rightEar?.wordList &&
      report.speechAudiometryReport?.rightEar?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          rightEar: {
            presentation: pref?.speech_audiometry?.presentation,
            type: report.speechAudiometryReport?.rightEar?.type,
            wordList: report.speechAudiometryReport?.rightEar?.wordList
          }
        }
      });
    }
    setting.speechAudiometryPresentation = true;
    shouldUpdate = true;
  }

  //set word_recognition ear presentation
  if (
    !setting.speechWordRecognitionPresentation &&
    (updatedReport?.word_recognition_report?.is_selected ||
      report.wordRecognitionReport?.isSelected) &&
    isNotBlank(pref?.word_recognition?.presentation)
  ) {
    if (
      !report.wordRecognitionReport?.leftEar?.presentation &&
      !isEmpty(report.wordRecognitionReport?.leftEar)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          left_ear: {
            presentation: pref?.word_recognition?.presentation
          }
        }
      });
    }

    if (
      !report.wordRecognitionReport?.binaural?.presentation &&
      !isEmpty(report.wordRecognitionReport?.binaural)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          binaural: {
            presentation: pref?.word_recognition?.presentation
          }
        }
      });
    }

    if (
      !report.wordRecognitionReport?.rightEar?.presentation &&
      !isEmpty(report.wordRecognitionReport?.rightEar)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          right_ear: {
            presentation: pref?.word_recognition?.presentation
          }
        }
      });
    }
    setting.speechWordRecognitionPresentation = true;
    shouldUpdate = true;
  }

  //set speech_audiometry ear word_list
  if (
    !setting.speechAudiometryWordList &&
    (updatedReport?.speech_audiometry_report?.is_selected ||
      report.speechAudiometryReport?.isSelected) &&
    pref?.speech_audiometry?.word_list
  ) {
    if (
      !report.speechAudiometryReport?.leftEar?.wordList &&
      report.speechAudiometryReport?.leftEar?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          left_ear: {
            wordList: pref?.speech_audiometry?.word_list
          }
        }
      });
    }

    if (
      !report.speechAudiometryReport?.binaural?.wordList &&
      report.speechAudiometryReport?.binaural?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          binaural: {
            wordList: pref?.speech_audiometry?.word_list
          }
        }
      });
    }

    if (
      !report.speechAudiometryReport?.rightEar?.wordList &&
      report.speechAudiometryReport?.rightEar?.type
    ) {
      updatedReport = merge(updatedReport, {
        speech_audiometry_report: {
          right_ear: {
            wordList: pref?.speech_audiometry?.word_list
          }
        }
      });
    }
    setting.speechAudiometryWordList = true;
    shouldUpdate = true;
  }

  //set word_recognition ear word_list
  if (
    !setting.wordRecognitionWordList &&
    (updatedReport?.word_recognition_report?.is_selected ||
      report.wordRecognitionReport?.isSelected) &&
    pref?.word_recognition?.word_list
  ) {
    if (
      !report.wordRecognitionReport?.leftEar?.wordList &&
      !isEmpty(report.wordRecognitionReport?.leftEar)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          left_ear: {
            wordList: pref?.word_recognition?.word_list
          }
        }
      });
    }

    if (
      !report.wordRecognitionReport?.binaural?.wordList &&
      !isEmpty(report.wordRecognitionReport?.binaural)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          binaural: {
            wordList: pref?.word_recognition?.word_list
          }
        }
      });
    }

    if (
      !report.wordRecognitionReport?.rightEar?.wordList &&
      !isEmpty(report.wordRecognitionReport?.rightEar)
    ) {
      updatedReport = merge(updatedReport, {
        word_recognition_report: {
          right_ear: {
            wordList: pref?.word_recognition?.word_list
          }
        }
      });
    }
    setting.wordRecognitionWordList = true;
    shouldUpdate = true;
  }

  return { setting, updatedReport, shouldUpdate };
};

const getDefaultSelectedReports = (user: MinimalStaffMember) => {
  const pref = user.user_preferences?.reporting;
  const result = {
    pure_tone_audiogram_overlay: getValueOrDefault(
      pref?.pure_tone_audiogram_overlay,
      true
    ),
    pure_tone_audiogram_report_selected: getValueOrDefault(
      pref?.pure_tone_audiogram_report_selected,
      true
    ),
    speech_audiometry_word_recognition_report_selected: getValueOrDefault(
      pref?.speech_audiometry_word_recognition_report_selected,
      true
    ),
    tympanometry_report_selected: getValueOrDefault(
      pref?.tympanometry_report_selected,
      true
    ),
    acoustic_reflex_decay_report_selected: getValueOrDefault(
      pref?.acoustic_reflex_decay_report_selected,
      true
    ),
    speech_in_noise_report_selected: getValueOrDefault(
      pref?.speech_in_noise_report_selected,
      true
    ),
    otoacoustic_emissions_report_selected: getValueOrDefault(
      pref?.otoacoustic_emissions_report_selected,
      true
    ),
    otoscopy_report_selected: getValueOrDefault(
      pref?.otoscopy_report_selected,
      true
    )
  };

  return result;
};

export const getDefaultTestsSetting = (
  user: MinimalStaffMember | null | undefined
) => {
  const result: Partial<AudiologyTestDefaults> = {};

  if (!user) return result;

  const {
    pure_tone_audiogram_report_selected,
    speech_audiometry_word_recognition_report_selected,
    tympanometry_report_selected,
    acoustic_reflex_decay_report_selected,
    speech_in_noise_report_selected,
    otoacoustic_emissions_report_selected,
    otoscopy_report_selected
  } = getDefaultSelectedReports(user);

  if (!pure_tone_audiogram_report_selected) {
    result.pure_tone_audiogram_report = {
      is_selected: false,
      is_air_conduction_l_selected: true,
      is_air_conduction_masking_level_l_selected: true,
      is_bone_conduction_l_selected: true,
      is_bone_conduction_masking_level_l_selected: true,
      is_mcl_l_selected: true,
      is_ucl_l_selected: true,
      is_sound_field_aided_l_selected: true,
      is_sound_field_unaided_l_selected: true,
      is_air_conduction_r_selected: true,
      is_air_conduction_masking_level_r_selected: true,
      is_bone_conduction_r_selected: true,
      is_bone_conduction_masking_level_r_selected: true,
      is_mcl_r_selected: true,
      is_ucl_r_selected: true,
      is_sound_field_aided_r_selected: true,
      is_sound_field_unaided_r_selected: true
    };
  }

  if (!speech_audiometry_word_recognition_report_selected) {
    result.speech_audiometry_report = { is_selected: false };
    result.word_recognition_report = { is_selected: false };
  }

  if (!tympanometry_report_selected) {
    result.tympanometry_report = { is_selected: false };
  }

  if (!acoustic_reflex_decay_report_selected) {
    result.acoustic_reflex_report = { is_selected: false };
    result.acoustic_reflex_decay_report = { is_selected: false };
  }

  if (!speech_in_noise_report_selected) {
    result.speech_in_noise_report = { is_selected: false };
  }

  if (!otoacoustic_emissions_report_selected) {
    result.otoacoustic_emissions_report = { is_selected: false };
  }

  if (!otoscopy_report_selected) {
    result.otoscopy_report = { is_selected: false };
  }

  return result;
};

export const buildReportIsSelectedUserPreferences = (
  userPreferences: any,
  report: Report
) => {
  const pref = userPreferences;

  // Check if OAE report already exists - if not, don't include it in the PATCH
  // to avoid creating it prematurely (which would set frequency_data that can't be updated)
  const isOaeReportCreated = report.otoacoustic_emissions_report !== null;

  const reportToUpdate: any = {
    pure_tone_audiogram_report: {
      is_selected: Boolean(pref.pure_tone_audiogram_report_selected),
      is_air_conduction_l_selected: true,
      is_air_conduction_masking_level_l_selected: true,
      is_bone_conduction_l_selected: true,
      is_bone_conduction_masking_level_l_selected: true,
      is_mcl_l_selected: true,
      is_ucl_l_selected: true,
      is_sound_field_aided_l_selected: true,
      is_sound_field_unaided_l_selected: true,
      is_air_conduction_r_selected: true,
      is_air_conduction_masking_level_r_selected: true,
      is_bone_conduction_r_selected: true,
      is_bone_conduction_masking_level_r_selected: true,
      is_mcl_r_selected: true,
      is_ucl_r_selected: true,
      is_sound_field_aided_r_selected: true,
      is_sound_field_unaided_r_selected: true
    },
    speech_audiometry_report: {
      is_selected: Boolean(
        pref.speech_audiometry_word_recognition_report_selected
      )
    },
    word_recognition_report: {
      is_selected: Boolean(
        pref.speech_audiometry_word_recognition_report_selected
      )
    },
    tympanometry_report: {
      is_selected: Boolean(pref.tympanometry_report_selected)
    },
    acoustic_reflex_report: {
      is_selected: Boolean(pref.acoustic_reflex_decay_report_selected)
    },
    acoustic_reflex_decay_report: {
      is_selected: Boolean(pref.acoustic_reflex_decay_report_selected)
    },
    speech_in_noise_report: {
      is_selected: Boolean(pref.speech_in_noise_report_selected)
    },
    // Only include OAE report if it already exists to avoid creating it prematurely
    ...(isOaeReportCreated && {
      otoacoustic_emissions_report: {
        is_selected: Boolean(pref.otoacoustic_emissions_report_selected)
      }
    }),
    otoscopy_report: {
      is_selected: Boolean(pref.otoscopy_report_selected)
    }
  };

  return {
    reportToUpdate,
    emitEventsFn: () => {
      // emit events to update React Hook Form is_selected state in Test Results
      eventBus.emit(PURE_TONE_AUDIOGRAM_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.pure_tone_audiogram_report?.is_selected)
      });
      eventBus.emit(SPEECH_AUDIOLOGY_WORD_RECOGNITION_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.speech_audiometry_report?.is_selected)
      });
      eventBus.emit(TYMPANOMETRY_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.tympanometry_report?.is_selected)
      });
      eventBus.emit(ACOUSTIC_REFLEX_DECAY_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.acoustic_reflex_report?.is_selected)
      });
      eventBus.emit(SPEECH_IN_NOISE_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.speech_in_noise_report?.is_selected)
      });
      eventBus.emit(OTOSCOPY_REPORT_SELECTED, {
        value: Boolean(reportToUpdate?.otoscopy_report?.is_selected)
      });
      // Only emit OAE event if OAE report exists (was included in reportToUpdate)
      if (isOaeReportCreated) {
        eventBus.emit(OTOACOUSTIC_EMISSIONS_REPORT_SELECTED, {
          value: Boolean(
            reportToUpdate?.otoacoustic_emissions_report?.is_selected
          )
        });
      }
    }
  };
};

const speechAudiometryHasData = (wordList: any, type: any) => {
  return Boolean(wordList && type);
};

const speechInNoiseHasData = (method: any, snrLossDegree: any) => {
  return Boolean(method && snrLossDegree);
};

export const buildReportDefaultUserPreferences = (
  userPreferences: any,
  report: Report,
  reportToUpdate: any
) => {
  const pref = userPreferences;
  const isBinaural =
    pref?.speech_in_noise?.quick_sin_default_configuration ===
    QUICK_SIN_CONFIGURATION_METHOD_BINAURAL;

  const eventsList: Function[] = [];

  //speech_in_noise binaural
  if (
    report.speechInNoiseReport?.isSelected &&
    speechInNoiseHasData(
      report.speechInNoiseReport?.binaural?.method,
      report.speechInNoiseReport?.binaural?.snrLossDegree
    )
  ) {
    reportToUpdate.speech_in_noise_report.binaural = {
      method: pref?.speech_in_noise?.method
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_IN_NOISE_REPORT_UPDATE_METHOD, {
        groupName: "binaural",
        value: pref?.speech_in_noise?.method
      });
    });
  }

  //speech_in_noise left
  if (
    report.speechInNoiseReport?.isSelected &&
    speechInNoiseHasData(
      report.speechInNoiseReport?.leftEar?.method,
      report.speechInNoiseReport?.leftEar?.snrLossDegree
    )
  ) {
    reportToUpdate.speech_in_noise_report.left_ear = {
      method: pref?.speech_in_noise?.method
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_IN_NOISE_REPORT_UPDATE_METHOD, {
        groupName: "leftEar",
        value: pref?.speech_in_noise?.method
      });
    });
  }

  //speech_in_noise right
  if (
    report.speechInNoiseReport?.isSelected &&
    speechInNoiseHasData(
      report.speechInNoiseReport?.rightEar?.method,
      report.speechInNoiseReport?.rightEar?.snrLossDegree
    )
  ) {
    reportToUpdate.speech_in_noise_report.right_ear = {
      method: pref?.speech_in_noise?.method
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_IN_NOISE_REPORT_UPDATE_METHOD, {
        groupName: "rightEar",
        value: pref?.speech_in_noise?.method
      });
    });
  }

  const speechAudiometryBinauralHasData = speechAudiometryHasData(
    report.speechAudiometryReport?.binaural?.wordList,
    report.speechAudiometryReport?.binaural?.type
  );
  const speechAudiometryLeftHasData = speechAudiometryHasData(
    report.speechAudiometryReport?.leftEar?.wordList,
    report.speechAudiometryReport?.leftEar?.type
  );
  const speechAudiometryRightHasData = speechAudiometryHasData(
    report.speechAudiometryReport?.rightEar?.wordList,
    report.speechAudiometryReport?.rightEar?.type
  );
  if (
    report.speechAudiometryReport?.isSelected &&
    isNotBlank(pref?.speech_audiometry?.presentation)
  ) {
    //speech audiometry binaural
    if (speechAudiometryBinauralHasData) {
      reportToUpdate.speech_audiometry_report.binaural = {
        presentation: pref?.speech_audiometry?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION, {
          groupName: "binaural",
          value: pref?.speech_audiometry?.presentation
        });
      });
    }

    //speech audiometry left
    if (speechAudiometryLeftHasData) {
      reportToUpdate.speech_audiometry_report.leftEar = {
        presentation: pref?.speech_audiometry?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION, {
          groupName: "leftEar",
          value: pref?.speech_audiometry?.presentation
        });
      });
    }

    //speech audiometry right
    if (speechAudiometryRightHasData) {
      reportToUpdate.speech_audiometry_report.rightEar = {
        presentation: pref?.speech_audiometry?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION, {
          groupName: "rightEar",
          value: pref?.speech_audiometry?.presentation
        });
      });
    }
  }

  if (
    report.wordRecognitionReport?.isSelected &&
    isNotBlank(pref?.word_recognition?.presentation)
  ) {
    //word_recognition binaural
    if (speechAudiometryBinauralHasData || isBinaural) {
      reportToUpdate.word_recognition_report.binaural = {
        presentation: pref?.word_recognition?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION, {
          groupName: "binaural",
          value: pref?.word_recognition?.presentation
        });
      });
    }

    //word_recognition left
    if (speechAudiometryLeftHasData || !isBinaural) {
      reportToUpdate.word_recognition_report.left_ear = {
        presentation: pref?.word_recognition?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION, {
          groupName: "leftEar",
          value: pref?.word_recognition?.presentation
        });
      });
    }

    //word_recognition right
    if (speechAudiometryRightHasData || !isBinaural) {
      reportToUpdate.word_recognition_report.right_ear = {
        presentation: pref?.word_recognition?.presentation
      };
      eventsList.push(() => {
        eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION, {
          groupName: "rightEar",
          value: pref?.word_recognition?.presentation
        });
      });
    }
  }

  //set speech_audiometry word_list
  if (
    report.speechAudiometryReport?.isSelected &&
    pref?.speech_audiometry?.word_list
  ) {
    //speech audiometry binaural
    reportToUpdate.speech_audiometry_report.binaural = {
      ...reportToUpdate.speech_audiometry_report.binaural,
      wordList: pref?.speech_audiometry?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST, {
        groupName: "binaural",
        value: pref?.speech_audiometry?.word_list
      });
    });

    //speech audiometry left
    reportToUpdate.speech_audiometry_report.leftEar = {
      ...reportToUpdate.speech_audiometry_report.leftEar,
      wordList: pref?.speech_audiometry?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST, {
        groupName: "leftEar",
        value: pref?.speech_audiometry?.word_list
      });
    });

    //speech audiometry right
    reportToUpdate.speech_audiometry_report.rightEar = {
      ...reportToUpdate.speech_audiometry_report.rightEar,
      wordList: pref?.speech_audiometry?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST, {
        groupName: "rightEar",
        value: pref?.speech_audiometry?.word_list
      });
    });
  }

  //set word_recognition word_list
  if (
    report.wordRecognitionReport?.isSelected &&
    pref?.word_recognition?.word_list
  ) {
    //word_recognition binaural
    reportToUpdate.word_recognition_report.binaural = {
      ...reportToUpdate.word_recognition_report.binaural,
      wordList: pref?.word_recognition?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST, {
        groupName: "binaural",
        value: pref?.word_recognition?.word_list
      });
    });

    //word_recognition left
    reportToUpdate.word_recognition_report.left_ear = {
      ...reportToUpdate.word_recognition_report.left_ear,
      wordList: pref?.word_recognition?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST, {
        groupName: "leftEar",
        value: pref?.word_recognition?.word_list
      });
    });

    //word_recognition right
    reportToUpdate.word_recognition_report.right_ear = {
      ...reportToUpdate.word_recognition_report.right_ear,
      wordList: pref?.word_recognition?.word_list
    };
    eventsList.push(() => {
      eventBus.emit(WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST, {
        groupName: "rightEar",
        value: pref?.word_recognition?.word_list
      });
    });
  }

  return {
    reportToUpdate,
    emitEventsFn: () => {
      eventsList.forEach((event) => event());
    }
  };
};
